import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pick } from 'lodash';

import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import {
  LanguageDetails,
  languageDetails,
} from '../../../constants/languageDetails';
import { Regions, RegionsCode, WorldwideCode } from '../../../constants/region';
import { emailSchema } from '../../../constants/validationSchemas';
import {
  getWebPageLink,
  getWebPageVariant,
  WebPageLink,
  WebPageType,
} from '../../../constants/webPageType';
import { usePopup, useShareData } from '../../../context';
import useRedirectEntry from '../../../hooks/useRedirectEntry';
import { TranslationLocale } from '../../../i18n';
import { TtsLocale, UiLocale } from '../../../models/Base';
import { Regions as TranslatedRegions } from '../../../models/SystemStore/SystemStore';
import { PhoneVerificationJson } from '../../../models/UserStore/PhoneVerification';
import {
  ResetPasswordResult,
  UpdateCurrentUserResult,
} from '../../../models/UserStore/UserStore';
import { useSessionStore } from '../../../stores/SessionStore';
import { getRegionsList } from '../../../stores/SystemStore';
import { useUserStore } from '../../../stores/UserStore';
import { getUiLocale } from '../../../utils/i18n';
import { getRegionFromPhoneCode, getRegionInfo } from '../../../utils/region';
import { PhoneCodePopup } from '../../Popups/PhoneCodePopup';
import { PhoneVerificationTemplate } from '../../Templates';
import {
  AppBar,
  AvatarNode,
  CircleCheckbox,
  JoyajoyImage,
  ListItemProps,
  LoadingSpinner,
  SettingList,
  SvgIcon,
  TextButton,
  TextButtonSize,
  TextInput,
} from '..';

import styles from './AccountSettingPages.module.css';

interface DisplayedNameState {
  displayedName: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
}

interface PasswordState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
}

interface AccountSettingPagesProps {
  webPageType: WebPageType;
}

const { sendPhoneVerification, validatePhone, updateCurrentUser } =
  useUserStore.getState();

export const SetDisplayNameScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userId = useSessionStore((state) => state.activeUserId);

    const MAX_DISPLAYNAME = 20;

    const { setSharedData } = useShareData();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

    const [state, setState] = useState<DisplayedNameState>({
      displayedName: userProfile?.name ?? '',
      isClicked: false,
      errorMessage: '',
      isLoading: false,
    });

    useRedirectEntry({ webPageType, allowTrial: false });

    const onDisplayedNameChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, displayedName: event.target.value }));
      },
      [],
    );
    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const onClickConfirm = useCallback(async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const res = await updateCurrentUser({
        user: {
          name: state.displayedName,
        },
      });

      if (res === UpdateCurrentUserResult.Success) {
        navigate(getWebPageLink(webPageType, WebPageLink.Account));
      } else {
        onSetErrorMessage('Failed to update display name');
      }

      setState((s) => ({ ...s, isLoading: false }));
    }, [state.displayedName, navigate, webPageType, onSetErrorMessage]);

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Display Name')}</p>
          {userProfile && <AvatarNode profile={userProfile} size={180} />}
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.displayedName}
              onChange={onDisplayedNameChange}
              placeholder={t('Display Name')}
              isClicked={state.isClicked}
              maxLength={MAX_DISPLAYNAME}
            />
            <div className={styles.warning}>{t(state.errorMessage)}</div>
          </div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={() => {
                onClickConfirm().catch(console.error);
              }}
              disabled={state.displayedName.length > MAX_DISPLAYNAME}
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);

// separate

export const SetUsernameScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setSharedData } = useShareData();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const MIN_USERNAME = 3;
    const MAX_USERNAME = 20;

    const userId = useSessionStore((state) => state.activeUserId);
    const [state, setState] = useState({
      newUsername: '',
      currentPassword: '',
      isClicked: false,
      errorMessage: '',
      isLoading: false,
      showRedBorder: false,
    });

    useRedirectEntry({ allowTrial: false });

    const userAccount = useUserStore((store) => store.getAccount(userId ?? ''));

    const listData = [
      {
        title: t('Email'),
        description: '',
        onPress: () => {},
        hover: false,
        rightComponent: (
          <div className="right-component">
            {userAccount?.email?.isVerified && (
              <div className="tick-container">
                <SvgIcon
                  name={getWebPageVariant(webPageType).circleTick}
                  width={24}
                  height={24}
                  color="white"
                />
              </div>
            )}
            {userAccount?.email?.address || t('Email not set')}
          </div>
        ),
      },
    ];

    const onNewUsernameChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, newUsername: event.target.value }));
        const hasInvalidChars = /[^0-9a-zA-Z_]/.test(event.target.value);
        if (hasInvalidChars) {
          setState((s) => ({
            ...s,
            showRedBorder: true,
            errorMessage:
              'Only letters (A-Z, a-z), numbers (0-9), and underscores (_) are allowed.',
          }));
        } else {
          setState((s) => ({
            ...s,
            showRedBorder: false,
            errorMessage: '',
          }));
        }
      },
      [],
    );

    const onCurrentPasswordChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, currentPassword: event.target.value }));
      },
      [],
    );

    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const onClickForgot = useCallback(() => {
      navigate(getWebPageLink(webPageType, WebPageLink.ForgotPassword));
    }, [navigate, webPageType]);

    const onClickConfirm = useCallback(async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));

      const res = await updateCurrentUser({
        user: {
          username: state.newUsername,
        },
        password: state.currentPassword,
      });

      if (res === UpdateCurrentUserResult.Success) {
        navigate(getWebPageLink(webPageType, WebPageLink.Account));
      } else {
        if (res === UpdateCurrentUserResult.InvalidCredentials) {
          onSetErrorMessage('Invalid password');
        } else if (res === UpdateCurrentUserResult.UsernameAlreadyRegistered) {
          onSetErrorMessage('This username is already taken. Try another.');
        } else {
          onSetErrorMessage(
            'Only letters (A-Z, a-z), numbers (0-9), and underscores (_) are allowed.',
          );
        }
      }

      setState((s) => ({ ...s, isLoading: false }));
    }, [
      state.newUsername,
      state.currentPassword,
      navigate,
      webPageType,
      onSetErrorMessage,
    ]);

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Username')}</p>
          <div className={styles.settingList}>
            <SettingList data={listData} />
          </div>
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.newUsername}
              onChange={onNewUsernameChange}
              placeholder={t('New Username')}
              isClicked={state.isClicked}
              maxLength={MAX_USERNAME}
              minLength={MIN_USERNAME}
              showRedBorder={state.showRedBorder}
            />
            <TextInput
              value={state.currentPassword}
              onChange={onCurrentPasswordChange}
              placeholder={t('Current Password')}
              isClicked={state.isClicked}
              type="password"
            />
            <div className={styles.forgotPasswordContainer}>
              <p
                className={`${styles.forgotPassword} ${
                  styles[getWebPageVariant(webPageType).classVariantLight1]
                }`}
                onClick={onClickForgot}
              >
                {t('Forgotten Password?')}
              </p>
            </div>
            <div className={styles.warning}>{t(state.errorMessage)}</div>
          </div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={() => {
                onClickConfirm().catch(console.error);
              }}
              disabled={
                state.newUsername.length < MIN_USERNAME ||
                state.currentPassword.length === 0 ||
                state.newUsername.length > MAX_USERNAME ||
                /[^0-9a-zA-Z_]/.test(state.newUsername)
              }
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);

export const SetEmailScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setSharedData } = useShareData();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const userId = useSessionStore((state) => state.activeUserId);
    const [state, setState] = useState({
      newEmail: '',
      confirmEmail: '',
      currentPassword: '',
      isClicked: false,
      errorMessage: '',
      isLoading: false,
    });

    useRedirectEntry({ allowTrial: false });

    const { username, email } = useUserStore((userState) => {
      const account = userState.getAccount(userId ?? '');
      return {
        ...pick(account, ['username', 'email']),
      };
    });

    const listData = [
      {
        title: t('Username'),
        description: username || t('error'),
        onPress: () => {},
        hover: false,
      },
      {
        title: t('Email'),
        description: '',
        onPress: () => {},
        hover: false,
        rightComponent: (
          <div className="right-component">
            {email?.isVerified && (
              <div className="tick-container">
                <SvgIcon
                  name={getWebPageVariant(webPageType).circleTick}
                  width={24}
                  height={24}
                  color="white"
                />
              </div>
            )}
            {email?.address || t('Email not set')}
          </div>
        ),
      },
    ];

    const onNewEmailChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, newEmail: event.target.value }));
      },
      [],
    );

    const onConfirmEmailChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, confirmEmail: event.target.value }));
      },
      [],
    );

    const onCurrentPasswordChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, currentPassword: event.target.value }));
      },
      [],
    );

    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const onClickForgot = useCallback(() => {
      navigate(getWebPageLink(webPageType, WebPageLink.ForgotPassword));
    }, [navigate, webPageType]);

    const onClickConfirm = useCallback(async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const validateResult = emailSchema.safeParse(
        state.confirmEmail ?? state.newEmail,
      );

      if (!validateResult.success) {
        onSetErrorMessage('Please enter a valid email address.');
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      if (state.newEmail !== state.confirmEmail) {
        onSetErrorMessage(t('New emails do not match'));
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      const res = await updateCurrentUser({
        user: {
          email: state.newEmail,
        },
        password: state.currentPassword,
      });

      if (res === UpdateCurrentUserResult.Success) {
        navigate(getWebPageLink(webPageType, WebPageLink.Account));
      } else {
        if (res === UpdateCurrentUserResult.InvalidCredentials) {
          onSetErrorMessage('Invalid password');
        } else if (res === UpdateCurrentUserResult.EmailAlreadyRegistered) {
          onSetErrorMessage('This email is already taken. Try another.');
        } else {
          onSetErrorMessage('There is something wrong.');
        }
      }

      setState((s) => ({ ...s, isLoading: false }));
    }, [
      state.confirmEmail,
      state.newEmail,
      state.currentPassword,
      onSetErrorMessage,
      t,
      navigate,
      webPageType,
    ]);

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Email')}</p>
          <div className={styles.settingList}>
            <SettingList data={listData} />
          </div>
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.newEmail}
              onChange={onNewEmailChange}
              placeholder={t('New Email')}
              isClicked={state.isClicked}
            />
            <TextInput
              value={state.confirmEmail}
              onChange={onConfirmEmailChange}
              placeholder={t('Retype New Email')}
              isClicked={state.isClicked}
            />
            <TextInput
              value={state.currentPassword}
              onChange={onCurrentPasswordChange}
              placeholder={t('Current Password')}
              isClicked={state.isClicked}
              type="password"
            />
            <div className={styles.forgotPasswordContainer}>
              <p
                className={`${styles.forgotPassword} ${
                  styles[getWebPageVariant(webPageType).classVariantLight1]
                }`}
                onClick={onClickForgot}
              >
                {t('Forgotten Password?')}
              </p>
            </div>
            <div className={styles.warning}>{t(state.errorMessage)}</div>
          </div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={() => {
                onClickConfirm().catch(console.error);
              }}
              disabled={
                state.newEmail.length === 0 ||
                state.confirmEmail.length === 0 ||
                state.currentPassword.length === 0
              }
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);

export const SetPhoneNumberScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { showPopup, hidePopup } = usePopup();
    const { setSharedData } = useShareData();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const userId = useSessionStore((state) => state.activeUserId);
    const [state, setState] = useState({
      phoneCode: '+852',
      phoneNumber: '',
      currentPassword: '',
      isClicked: false,
      errorMessage: '',
      isLoading: false,
      phoneVerification: undefined as PhoneVerificationJson | undefined,
    });

    useRedirectEntry({ allowTrial: false });

    const { username, phone } = useUserStore((userState) => {
      const account = userState.getAccount(userId ?? '');
      return {
        ...pick(account, ['username', 'phone']),
      };
    });

    const listData = [
      {
        title: t('Username'),
        description: username || t('error'),
        onPress: () => {},
        hover: false,
      },
      {
        title: t('Phone Number'),
        description: phone?.phoneNumber || NOT_APPLICABLE_STRING,
        onPress: () => {},
        hover: false,
      },
    ];

    const onPhoneNumberChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({
          ...s,
          phoneNumber: event.target.value,
          errorMessage: '',
        }));
      },
      [],
    );

    const onCurrentPasswordChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({
          ...s,
          currentPassword: event.target.value,
          errorMessage: '',
        }));
      },
      [],
    );

    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const onClickForgot = useCallback(() => {
      navigate(getWebPageLink(webPageType, WebPageLink.ForgotPassword));
    }, [navigate, webPageType]);

    const onClickConfirm = useCallback(async () => {
      setState((s) => ({ ...s, isClicked: true, isLoading: true }));
      const res = await sendPhoneVerification({
        region: getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
        phoneNumber: state.phoneNumber,
      });
      const phoneVerification = res.phoneVerification;
      if (phoneVerification) {
        setState((s) => ({
          ...s,
          phoneVerification: phoneVerification,
        }));
      } else if (
        res.result === UpdateCurrentUserResult.PhoneAlreadyRegistered
      ) {
        onSetErrorMessage(
          'This phone number is already registered with a JOYAJOY account.',
        );
      } else {
        onSetErrorMessage('Invalid phone number');
      }
      setState((s) => ({ ...s, isLoading: false }));
    }, [onSetErrorMessage, state.phoneCode, state.phoneNumber]);

    const onClickVerifyCode = useCallback(
      async (code: string) => {
        if (state.phoneVerification) {
          setState((s) => ({ ...s, isClicked: true, isLoading: true }));

          const p = {
            region: state.phoneVerification.region,
            phoneNumber: state.phoneVerification.phoneNumber,
            verificationCode: code,
          };
          const result = await validatePhone(p);
          if (result === ResetPasswordResult.Error) {
            onSetErrorMessage('Incorrect Code');
            setState((s) => ({ ...s, isLoading: false }));
            return;
          }

          const res = await updateCurrentUser({
            user: { phone: p },
            password: state.currentPassword,
          });
          if (res === UpdateCurrentUserResult.Success) {
            navigate(getWebPageLink(webPageType, WebPageLink.Account));
          } else {
            if (res === UpdateCurrentUserResult.InvalidCredentials) {
              onSetErrorMessage('Invalid password');
            } else if (res === UpdateCurrentUserResult.EmailAlreadyRegistered) {
              // TODO: phone already taken
              onSetErrorMessage('This email is already taken. Try another.');
            } else {
              onSetErrorMessage('There is something wrong.');
            }
          }

          setState((s) => ({
            ...s,
            phoneVerification: undefined,
            isLoading: false,
          }));
        }
      },
      [
        navigate,
        onSetErrorMessage,
        state.currentPassword,
        state.phoneVerification,
        webPageType,
      ],
    );

    const onClickPhoneCode = useCallback(() => {
      showPopup(
        <PhoneCodePopup
          phoneCode={state.phoneCode}
          onPrimary={hidePopup}
          onCode={(selectedCode) => {
            setState((prevState) => ({
              ...prevState,
              phoneCode: selectedCode,
            }));
            hidePopup();
          }}
          webPageType={webPageType}
        />,
      );
    }, [showPopup, state.phoneCode, hidePopup, webPageType]);

    if (state.phoneVerification) {
      const { phoneNumber, verificationSentAt } = state.phoneVerification;

      return (
        <PhoneVerificationTemplate
          phoneNumber={phoneNumber}
          resendCountDown={verificationSentAt + 60 * 1000}
          errorMessage={state.errorMessage ?? ''}
          isLoading={state.isLoading}
          webPageType={webPageType}
          onClickConfirm={(code) => {
            onClickVerifyCode(code).catch(console.error);
          }}
          onClickResend={() => {
            onClickConfirm().catch(console.error);
          }}
          onClickBack={() => {
            setState((s) => ({ ...s, phoneVerification: undefined }));
          }}
        />
      );
    }

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Phone Number')}</p>
          <div className={styles.settingList}>
            <SettingList data={listData} />
          </div>
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.phoneNumber}
              onChange={onPhoneNumberChange}
              placeholder={t('New Phone Number')}
              isClicked={state.isClicked}
              type="phone"
              phoneCode={state.phoneCode}
              onClickPhoneCode={onClickPhoneCode}
            />
            <TextInput
              value={state.currentPassword}
              onChange={onCurrentPasswordChange}
              placeholder={t('Current Password')}
              isClicked={state.isClicked}
              type="password"
            />
            <div className={styles.forgotPasswordContainer}>
              <p
                className={`${styles.forgotPassword} ${
                  styles[getWebPageVariant(webPageType).classVariantLight1]
                }`}
                onClick={onClickForgot}
              >
                {t('Forgotten Password?')}
              </p>
            </div>
            <div className={styles.warning}>{t(state.errorMessage)}</div>
          </div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={() => {
                onClickConfirm().catch(console.error);
              }}
              disabled={
                state.phoneNumber.length === 0 ||
                state.currentPassword.length === 0
              }
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);

export const SetPasswordScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setSharedData } = useShareData();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const userId = useSessionStore((state) => state.activeUserId);
    const [state, setState] = useState<PasswordState>({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      isClicked: false,
      errorMessage: '',
      isLoading: false,
    });

    useRedirectEntry({ allowTrial: false });

    const { username, email } = useUserStore((userState) => {
      const account = userState.getAccount(userId ?? '');
      return {
        ...pick(account, ['username', 'email']),
      };
    });

    const listData: ListItemProps[] = [
      {
        title: t('Username'),
        description: username || t('error'),
        onPress: () => {},
        hover: false,
      },
      {
        title: t('Email'),
        description: '',
        onPress: () => {},
        hover: false,
        rightComponent: (
          <div className="right-component">
            {email?.isVerified && (
              <div className="tick-container">
                <SvgIcon
                  name={getWebPageVariant(webPageType).circleTick}
                  width={24}
                  height={24}
                  color="white"
                />
              </div>
            )}
            {email?.address || t('Email not set')}
          </div>
        ),
      },
    ];

    const onCurrentPasswordChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, currentPassword: event.target.value }));
      },
      [],
    );

    const onClickForgot = useCallback(() => {
      navigate(getWebPageLink(webPageType, WebPageLink.ForgotPassword));
    }, [navigate, webPageType]);

    const onNewPasswordChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, newPassword: event.target.value }));
      },
      [],
    );

    const onConfirmPasswordChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((s) => ({ ...s, confirmPassword: event.target.value }));
      },
      [],
    );

    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const onClickConfirm = useCallback(() => {
      const handleConfirm = async () => {
        setState((s) => ({ ...s, isClicked: true, isLoading: true }));

        if (state.newPassword !== state.confirmPassword) {
          onSetErrorMessage(t('New passwords do not match'));
          setState((s) => ({ ...s, isLoading: false }));
          return;
        }

        const res = await updateCurrentUser({
          user: {
            password: state.newPassword,
          },
          password: state.currentPassword,
        });

        if (res === UpdateCurrentUserResult.Success) {
          navigate(getWebPageLink(webPageType, WebPageLink.Account));
        } else {
          if (res === UpdateCurrentUserResult.InvalidCredentials) {
            onSetErrorMessage('Invalid password');
          } else {
            onSetErrorMessage('There is something wrong.');
          }
        }

        setState((s) => ({ ...s, isLoading: false }));
      };

      handleConfirm().catch(console.error);
    }, [
      state.newPassword,
      state.confirmPassword,
      state.currentPassword,
      onSetErrorMessage,
      t,
      navigate,
      webPageType,
    ]);

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Password')}</p>
          <div className={styles.settingList}>
            <SettingList data={listData} />
          </div>
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.currentPassword}
              onChange={onCurrentPasswordChange}
              placeholder={t('Current Password')}
              isClicked={state.isClicked}
              type="password"
            />
            <div className={styles.forgotPasswordContainer}>
              <p
                className={`${styles.forgotPassword} ${
                  styles[getWebPageVariant(webPageType).classVariantLight1]
                }`}
                onClick={onClickForgot}
              >
                {t('Forgotten Password?')}
              </p>
            </div>
            <TextInput
              value={state.newPassword}
              onChange={onNewPasswordChange}
              placeholder={t('New Password')}
              isClicked={state.isClicked}
              type="password"
            />
            <TextInput
              value={state.confirmPassword}
              onChange={onConfirmPasswordChange}
              placeholder={t('Retype New Password')}
              isClicked={state.isClicked}
              type="password"
            />
            <div className={styles.warning}>{t(state.errorMessage)}</div>
          </div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={onClickConfirm}
              disabled={
                state.currentPassword.length === 0 ||
                state.newPassword.length === 0 ||
                state.confirmPassword.length === 0 //||
                // state.newPassword !== state.confirmPassword
              }
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);

export const SetRegionScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const { t, i18n } = useTranslation();
    const activeRegion = useSessionStore((store) => store.activeRegion);
    const position = useSessionStore((store) => store.scrolledPos);

    const { setSharedData } = useShareData();
    const navigate = useNavigate();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const userId = useSessionStore((state) => state.activeUserId);
    const userProfile = useUserStore((state) => state.getProfile(userId ?? ''));

    const [state, setState] = useState({
      errorMessage: '',
      isLoading: false,
      searchTerm: '',
      selectedCountry: userProfile?.region ?? activeRegion,
      filteredRegions: [] as typeof Regions,
      translatedRegions: {} as TranslatedRegions,
    });

    const fetchTranslatedRegions = useCallback(async () => {
      const regions = await getRegionsList(
        getUiLocale(i18n.language as TranslationLocale),
      );
      setState((s) => ({ ...s, translatedRegions: regions }));
    }, [i18n.language]);

    useEffect(() => {
      fetchTranslatedRegions().catch(console.error);
    }, [fetchTranslatedRegions, i18n.language]);

    useEffect(() => {
      const filtered = Regions.filter(
        (region) =>
          region.id !== WorldwideCode &&
          region.country &&
          (state.translatedRegions[region.id]
            ?.toLowerCase()
            .includes(state.searchTerm.toLowerCase()) ||
            region.country
              .toLowerCase()
              .includes(state.searchTerm.toLowerCase())),
      );

      // Sort the filtered regions
      const sortedRegions = filtered.sort((a, b) => {
        const nameA = state.translatedRegions[a.id] || a.country || '';
        const nameB = state.translatedRegions[b.id] || b.country || '';
        return nameA.localeCompare(nameB, i18n.language);
      });

      setState((s) => ({ ...s, filteredRegions: sortedRegions }));
    }, [i18n.language, state.translatedRegions, state.searchTerm]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, searchTerm: event.target.value }));
    };

    const handleSelect = (regionCode: RegionsCode) => {
      setState((s) => ({ ...s, selectedCountry: regionCode }));
    };

    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const handleConfirm = useCallback(async () => {
      if (state.selectedCountry) {
        useSessionStore.getState().setActiveRegion(state.selectedCountry);
        if (!userId) {
          navigate(-1);
          return;
        }

        setState((s) => ({ ...s, isLoading: true }));

        const res = await updateCurrentUser({
          user: {
            region: state.selectedCountry,
          },
        });

        if (res === UpdateCurrentUserResult.Success) {
          // navigate('/settings/account');
          navigate(-1);
          if (position) {
            const tryScroll = (attempts = 0) => {
              if (attempts > 5) {
                return;
              }
              setTimeout(() => {
                document.documentElement.style.scrollBehavior = 'auto';
                document.body.style.scrollBehavior = 'auto';
                document.documentElement.scrollTop = position;
                document.body.scrollTop = position;
                if (window.scrollY !== position) {
                  tryScroll(attempts + 1);
                } else {
                  useSessionStore.getState().setScrollPos(null);
                }
              }, 100);
            };
            tryScroll();
          }
        } else {
          onSetErrorMessage('There is something wrong.');
        }

        setState((s) => ({ ...s, isLoading: false }));
      }
    }, [state.selectedCountry, userId, navigate, position, onSetErrorMessage]);

    const getRegionImage = (regionId: RegionsCode | typeof WorldwideCode) => {
      if (regionId === WorldwideCode) {
        return Regions.find((r) => r.id === WorldwideCode)?.image || '';
      }
      return getRegionInfo(regionId).image || '';
    };

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Country / Region')}</p>
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.searchTerm}
              onChange={handleSearch}
              placeholder={t('Search countries...')}
            />
            <div className={styles.regionList}>
              {state.filteredRegions.map((region, index) => (
                <div key={region.id}>
                  <div
                    className={styles.regionItem}
                    onClick={() => handleSelect(region.id as RegionsCode)}
                  >
                    <JoyajoyImage
                      resource={{ uri: getRegionImage(region.id) }}
                      className={styles.regionImage}
                    />
                    <div className={styles.regionText}>
                      {state.translatedRegions[region.id] || region.country}
                    </div>

                    <CircleCheckbox
                      webPageType={webPageType}
                      checked={state.selectedCountry === region.id}
                    />
                  </div>
                  {index < state.filteredRegions.length - 1 && (
                    <hr style={{ width: '96%', margin: '0 auto' }} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.warning}>{t(state.errorMessage)}</div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={() => {
                handleConfirm().catch(console.error);
              }}
              disabled={!state.selectedCountry}
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);

export const SetLanguageScreen: React.FC<AccountSettingPagesProps> = memo(
  ({ webPageType }) => {
    const { t, i18n } = useTranslation();
    const position = useSessionStore((store) => store.scrolledPos);

    const { setSharedData } = useShareData();
    const navigate = useNavigate();

    useEffect(() => {
      setSharedData(undefined);
    }, [setSharedData]);

    const userId = useSessionStore((state) => state.activeUserId);
    const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));

    const [state, setState] = useState({
      errorMessage: '',
      isLoading: false,
      searchTerm: '',
      selectedLocale: getUiLocale(i18n.language as TranslationLocale),
    });

    const UiLocaleArray = Object.values(UiLocale).map((key) => key);

    const languageLists = Object.fromEntries(
      Object.entries(languageDetails).filter(([locale]) => {
        return UiLocaleArray.includes(locale as UiLocale);
      }),
    ) as Record<TtsLocale, LanguageDetails>;

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, searchTerm: event.target.value }));
    };

    const handleSelect = (locale: UiLocale) => {
      setState((s) => ({ ...s, selectedLocale: locale }));
    };

    const onSetErrorMessage = useCallback((message: string) => {
      setState((s) => ({ ...s, errorMessage: message }));
    }, []);

    const handleConfirm = useCallback(async () => {
      if (state.selectedLocale) {
        i18n.changeLanguage(state.selectedLocale).catch(console.error);
        if (!userId) {
          navigate(-1);
          return;
        }

        if (userAccount) {
          setState((s) => ({ ...s, isLoading: true }));

          const res = await updateCurrentUser({
            user: {
              settings: {
                ...userAccount.settings,
                interfaceLocale: state.selectedLocale,
              },
            },
          });

          if (res === UpdateCurrentUserResult.Success) {
            // navigate('/settings/account');
            navigate(-1);
            if (position) {
              const tryScroll = (attempts = 0) => {
                if (attempts > 5) {
                  return;
                }
                setTimeout(() => {
                  document.documentElement.style.scrollBehavior = 'auto';
                  document.body.style.scrollBehavior = 'auto';
                  document.documentElement.scrollTop = position;
                  document.body.scrollTop = position;
                  if (window.scrollY !== position) {
                    tryScroll(attempts + 1);
                  } else {
                    useSessionStore.getState().setScrollPos(null);
                  }
                }, 100);
              };
              tryScroll();
            }
          } else {
            onSetErrorMessage('There is something wrong.');
          }

          setState((s) => ({ ...s, isLoading: false }));
        }
      }
    }, [
      state.selectedLocale,
      userAccount,
      i18n,
      userId,
      navigate,
      position,
      onSetErrorMessage,
    ]);

    const filteredLanguages = Object.entries(languageLists).filter(
      ([, { displayName, nativeName }]) =>
        displayName.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
        nativeName.toLowerCase().includes(state.searchTerm.toLowerCase()),
    );

    return (
      <div className={styles.container}>
        <AppBar />
        <div className={styles.accountSetting}>
          <p className={styles.settingTitle}>{t('Interface Language')}</p>
          <div className={styles.centeredContainer}>
            <TextInput
              value={state.searchTerm}
              onChange={handleSearch}
              placeholder={t('Search')}
            />
            <div className={styles.languageList}>
              {filteredLanguages.map(
                ([key, { displayName, nativeName }], index) => (
                  <div key={key}>
                    <div
                      className={styles.regionItem}
                      onClick={() => handleSelect(key as UiLocale)}
                    >
                      <div className={styles.languageNames}>
                        <span className={styles.nativeName}>{nativeName}</span>
                        <span className={styles.displayName}>
                          {t(displayName)}
                        </span>
                      </div>

                      <CircleCheckbox
                        webPageType={webPageType}
                        checked={state.selectedLocale === (key as UiLocale)}
                      />
                    </div>
                    {index < filteredLanguages.length - 1 && (
                      <hr style={{ width: '96%', margin: '0 auto' }} />
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
          <div className={styles.warning}>{t(state.errorMessage)}</div>
          <div className={styles.confirmButton}>
            <TextButton
              text={t('Confirm')}
              size={TextButtonSize.Medium}
              variant={getWebPageVariant(webPageType).textButtonVariant}
              onClick={() => {
                handleConfirm().catch(console.error);
              }}
              disabled={!state.selectedLocale}
            />
          </div>
          {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
        </div>
      </div>
    );
  },
);
